import { useCallback, useEffect, useState } from 'react';
import { opsApi, adminApi } from '../../api/client';
import { CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, Save, Trash2, ShieldCheck } from 'lucide-react';

const CONNECTOR_ICONS = { active: CheckCircle, test_mode: Clock, configured: Clock, disabled: XCircle, not_connected: AlertTriangle };
const CONNECTOR_COLORS = { active: 'text-green-600 bg-green-50', test_mode: 'text-amber-600 bg-amber-50', configured: 'text-amber-600 bg-amber-50', disabled: 'text-gray-400 bg-gray-50', not_connected: 'text-gray-400 bg-gray-50' };
const WEBHOOK_COLORS = { RECEIVED: 'bg-blue-100 text-blue-700', PROCESSED: 'bg-green-100 text-green-700', ERROR: 'bg-red-100 text-red-700' };

const inputClass = 'w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

export default function OpsSystem() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yukiCfg, setYukiCfg] = useState(null);
  const [yukiForm, setYukiForm] = useState({ yukiEnabled: 'false', yukiBaseUrl: '', yukiApplicationKey: '', yukiCompanyId: '', yukiPeppolAutoSend: 'false' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  // Active Ants v3 (canonical) — parallel to Yuki pattern
  const [aaCfg, setAaCfg] = useState(null);
  const [aaForm, setAaForm] = useState({ aaEnabled: false, aaBaseUrl: '', aaUsername: '', aaPassword: '', aaMode: 'readonly' });
  const [aaSaving, setAaSaving] = useState(false);
  const [aaTesting, setAaTesting] = useState(false);
  const [aaTestResult, setAaTestResult] = useState(null);
  // Resend Email connector
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailForm, setEmailForm] = useState({ resendEnabled: 'false', senderEmail: '', senderName: '', replyToEmail: '', resendApiKey: '' });
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  // Mollie payment connector
  const [mollieCfg, setMollieCfg] = useState(null);
  const [mollieForm, setMollieForm] = useState({ mollieApiKey: '' });
  const [mollieSaving, setMollieSaving] = useState(false);
  const [mollieResult, setMollieResult] = useState(null);
  // Mollie webhook secret (P0-2b fallback). Env takes priority;
  // this card writes the system_config fallback.
  const [mollieWebhookCfg, setMollieWebhookCfg] = useState(null);
  const [mollieWebhookForm, setMollieWebhookForm] = useState({ mollieWebhookSecret: '' });
  const [mollieWebhookSaving, setMollieWebhookSaving] = useState(false);
  const [mollieWebhookResult, setMollieWebhookResult] = useState(null);
  // bpost GeoLocator connector (service-point + parcel-locker surfaces)
  const [bpostCfg, setBpostCfg] = useState(null);
  const [bpostForm, setBpostForm] = useState({
    bpostLocatorBaseUrl: '',
    bpostLocatorPath: '',
    bpostSpPartnerId: '',
    bpostSpAppId: '',
    bpostPlPartnerId: '',
    bpostPlAppId: '',
    bpostLocatorSecurityHeaderName: '',
    bpostLocatorSecurityHeaderValue: '',
    bpostSpTypeBitmask: '',
    bpostPlTypeBitmask: '',
    bpostLocatorLanguage: '',
  });
  const [bpostSaving, setBpostSaving] = useState(false);
  const [bpostResult, setBpostResult] = useState(null);
  // Connector audit timeline (recent credential change events).
  const [auditEvents, setAuditEvents] = useState([]);

  const reload = useCallback(async () => {
    const [sys, ycfg, aacfg, ecfg, mcfg, mwhcfg, bcfg, audit] = await Promise.all([
      opsApi.system().then(r => r.data).catch(() => null),
      opsApi.yukiConfig().then(r => r.data).catch(() => null),
      opsApi.aaV3Config().then(r => r.data).catch(() => null),
      adminApi.emailConfig().then(r => r.data).catch(() => null),
      adminApi.mollieConfig().then(r => r.data).catch(() => null),
      adminApi.mollieWebhookConfig().then(r => r.data).catch(() => null),
      adminApi.bpostLocatorConfig().then(r => r.data).catch(() => null),
      adminApi.connectorAudit({ limit: 10 }).then(r => r.data?.events || []).catch(() => []),
    ]);
    setData(sys);
    setYukiCfg(ycfg);
    if (ycfg) {
      setYukiForm({
        yukiEnabled: ycfg.yukiEnabled || 'false',
        yukiBaseUrl: ycfg.yukiBaseUrl || '',
        yukiApplicationKey: '',
        yukiCompanyId: ycfg.yukiCompanyId || ycfg.yukiAdministrationId || '',
        yukiPeppolAutoSend: ycfg.yukiPeppolAutoSend || 'false',
      });
    }
    setAaCfg(aacfg);
    if (aacfg) {
      setAaForm({
        aaEnabled: !!aacfg.aaEnabled,
        aaBaseUrl: aacfg.aaBaseUrl || '',
        aaUsername: aacfg.aaUsername || '',
        aaPassword: '',
        aaMode: aacfg.aaMode || 'readonly',
      });
    }
    setEmailCfg(ecfg);
    if (ecfg) {
      setEmailForm({
        resendEnabled: ecfg.resendEnabled || 'false',
        senderEmail: ecfg.senderEmail || '',
        senderName: ecfg.senderName || '',
        replyToEmail: ecfg.replyToEmail || '',
        resendApiKey: '',
      });
    }
    setMollieCfg(mcfg);
    // Always reset the input field after reload — the saved key is
    // never echoed; the masked label below the input is the source of
    // truth for "what's currently stored".
    setMollieForm({ mollieApiKey: '' });
    // Same convention for the webhook-secret card.
    setMollieWebhookCfg(mwhcfg);
    setMollieWebhookForm({ mollieWebhookSecret: '' });
    setBpostCfg(bcfg);
    if (bcfg) {
      setBpostForm({
        bpostLocatorBaseUrl: bcfg.bpostLocatorBaseUrl || '',
        bpostLocatorPath: bcfg.bpostLocatorPath || '',
        bpostSpPartnerId: bcfg.bpostSpPartnerId || '',
        bpostSpAppId: bcfg.bpostSpAppId || '',
        bpostPlPartnerId: bcfg.bpostPlPartnerId || '',
        bpostPlAppId: bcfg.bpostPlAppId || '',
        bpostLocatorSecurityHeaderName: bcfg.bpostLocatorSecurityHeaderName || '',
        // Password-style fields are always cleared on reload — the
        // masked label is the source of truth for "what's stored".
        bpostLocatorSecurityHeaderValue: '',
        bpostSpTypeBitmask: bcfg.bpostSpTypeBitmask || '',
        bpostPlTypeBitmask: bcfg.bpostPlTypeBitmask || '',
        bpostLocatorLanguage: bcfg.bpostLocatorLanguage || '',
      });
    }
    setAuditEvents(audit);
  }, []);

  useEffect(() => { reload().finally(() => setLoading(false)); }, [reload]);

  const [peppolConfirmation, setPeppolConfirmation] = useState('');
  const saveYuki = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      await opsApi.yukiConfigUpdate({ ...yukiForm, peppolConfirmation });
      setPeppolConfirmation('');
      await reload();
      setTestResult({ success: true, message: 'Config saved' });
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const detailMsg = detail && typeof detail === 'object' ? (detail.message || detail.code) : detail;
      if (status === 401) {
        setTestResult({ success: false, message: 'Session expired — please log in again' });
      } else if (status === 403) {
        setTestResult({ success: false, message: 'Admin permission required' });
      } else {
        setTestResult({ success: false, message: detailMsg || `Save failed (HTTP ${status || 'unknown'})` });
      }
    } finally {
      setSaving(false);
    }
  };

  const testYuki = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await opsApi.yukiTest();
      setTestResult(data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const msg = e.response?.data?.message;
      if (status === 401) {
        setTestResult({ success: false, message: 'Session expired — please log in again', errorType: 'auth' });
      } else if (status === 403) {
        setTestResult({ success: false, message: 'Admin permission required', errorType: 'auth' });
      } else {
        setTestResult({ success: false, message: msg || detail || `Test failed (HTTP ${status || 'unknown'})`, errorType: 'transport' });
      }
    } finally {
      setTesting(false);
    }
  };

  const previewCleanup = async () => {
    try {
      const { data } = await opsApi.cleanupPreview();
      setCleanupPreview(data);
    } catch (e) {
      setCleanupResult({ ok: false, message: e.response?.data?.detail || 'Preview failed — check login' });
    }
  };

  const executeCleanup = async () => {
    setCleaning(true);
    try {
      const { data } = await opsApi.cleanupExecute();
      setCleanupResult(data);
      setCleanupPreview(null);
      await reload();
    } catch (e) {
      setCleanupResult({ ok: false, message: e.response?.data?.detail || 'Cleanup failed' });
    }
    setCleaning(false);
  };

  const saveAa = async () => {
    setAaSaving(true);
    setAaTestResult(null);
    try {
      // Build patch: only include password when user typed one (blank preserves current).
      const patch = {
        aaEnabled: aaForm.aaEnabled, aaBaseUrl: aaForm.aaBaseUrl,
        aaUsername: aaForm.aaUsername, aaMode: aaForm.aaMode,
      };
      if (aaForm.aaPassword) patch.aaPassword = aaForm.aaPassword;
      await opsApi.aaV3ConfigUpdate(patch);
      await reload();
      setAaTestResult({ ok: true, message: 'Config saved' });
    } catch (e) {
      const status = e.response?.status;
      setAaTestResult({ ok: false, message: e.response?.data?.detail || `Save failed (HTTP ${status || 'unknown'})` });
    } finally {
      setAaSaving(false);
    }
  };

  const testAa = async () => {
    setAaTesting(true);
    setAaTestResult(null);
    try {
      const { data } = await opsApi.aaV3ConnectionTest();
      const ok = data.statusReachable && data.tokenAcquired;
      const bits = [
        data.statusReachable ? 'Status reachable' : 'Status unreachable',
        data.tokenAcquired ? 'Token acquired' : 'Token failed',
      ];
      setAaTestResult({ ok, message: `${bits.join(' · ')} — ${data.tokenDetail || ''}` });
    } catch (e) {
      setAaTestResult({ ok: false, message: e.response?.data?.detail || 'Test failed' });
    } finally {
      setAaTesting(false);
    }
  };

  const saveEmail = async () => {
    setEmailSaving(true);
    setEmailTestResult(null);
    try {
      const patch = {
        resendEnabled: emailForm.resendEnabled,
        senderEmail: emailForm.senderEmail,
        senderName: emailForm.senderName,
        replyToEmail: emailForm.replyToEmail,
      };
      // Only send a key if the admin actually typed one — blank
      // preserves the existing secret on the server.
      if (emailForm.resendApiKey) patch.resendApiKey = emailForm.resendApiKey;
      await adminApi.emailConfigUpdate(patch);
      await reload();
      setEmailTestResult({ ok: true, message: 'Config saved' });
    } catch (e) {
      const status = e.response?.status;
      setEmailTestResult({ ok: false, message: e.response?.data?.detail || `Save failed (HTTP ${status || 'unknown'})` });
    } finally {
      setEmailSaving(false);
    }
  };

  const testEmail = async () => {
    setEmailTesting(true);
    setEmailTestResult(null);
    try {
      const payload = emailRecipient ? { recipient: emailRecipient } : {};
      const { data } = await adminApi.emailTest(payload);
      const tag = data.transport === 'resend' ? 'sent via Resend' : 'console only';
      setEmailTestResult({
        ok: !!data.ok,
        message: `${data.message} — ${tag} → ${data.recipient}`,
      });
    } catch (e) {
      const status = e.response?.status;
      setEmailTestResult({ ok: false, message: e.response?.data?.detail || `Test failed (HTTP ${status || 'unknown'})` });
    } finally {
      setEmailTesting(false);
    }
  };

  const saveMollie = async () => {
    setMollieSaving(true);
    setMollieResult(null);
    try {
      const submitted = (mollieForm.mollieApiKey || '').trim();
      if (!submitted) {
        setMollieResult({ ok: false, message: 'Voer een API key in (test_… of live_…) of gebruik "Wissen".' });
        return;
      }
      const { data } = await adminApi.mollieConfigUpdate({ mollieApiKey: submitted });
      await reload();
      const modeLabel = data?.mode ? data.mode.toUpperCase() : 'unknown';
      setMollieResult({ ok: true, message: `Opgeslagen — modus: ${modeLabel}, decision: ${data?.decision || 'updated'}` });
    } catch (e) {
      const status = e.response?.status;
      setMollieResult({ ok: false, message: e.response?.data?.detail || `Save failed (HTTP ${status || 'unknown'})` });
    } finally {
      setMollieSaving(false);
    }
  };

  const clearMollie = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Weet je zeker dat je de opgeslagen Mollie key wilt wissen? Het systeem valt dan terug op de env-waarde (indien aanwezig).')) return;
    setMollieSaving(true);
    setMollieResult(null);
    try {
      const { data } = await adminApi.mollieConfigUpdate({ mollieApiKey: '', clear: true });
      await reload();
      setMollieResult({ ok: true, message: `Sleutel gewist — actieve bron: ${data?.source || 'none'}` });
    } catch (e) {
      const status = e.response?.status;
      setMollieResult({ ok: false, message: e.response?.data?.detail || `Clear failed (HTTP ${status || 'unknown'})` });
    } finally {
      setMollieSaving(false);
    }
  };

  const saveMollieWebhook = async () => {
    setMollieWebhookSaving(true);
    setMollieWebhookResult(null);
    try {
      const submitted = (mollieWebhookForm.mollieWebhookSecret || '').trim();
      if (!submitted) {
        // Server treats blank as "preserve existing", but we surface
        // a clear hint so the admin knows nothing was sent.
        setMollieWebhookResult({ ok: false, message: 'Laat dit veld niet leeg — bestaande waarde blijft behouden. Plak minimaal 24 tekens om te overschrijven.' });
        return;
      }
      if (submitted.length < 24) {
        setMollieWebhookResult({ ok: false, message: `Minimaal 24 tekens vereist (nu ${submitted.length}).` });
        return;
      }
      const { data } = await adminApi.mollieWebhookConfigUpdate({ mollieWebhookSecret: submitted });
      await reload();
      setMollieWebhookResult({ ok: true, message: `Opgeslagen — bron: ${data?.source || 'system_config'}, decision: ${data?.decision || 'updated'}` });
    } catch (e) {
      const status = e.response?.status;
      setMollieWebhookResult({ ok: false, message: e.response?.data?.detail || `Save failed (HTTP ${status || 'unknown'})` });
    } finally {
      setMollieWebhookSaving(false);
    }
  };

  const clearMollieWebhook = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Weet je zeker dat je het opgeslagen webhook-secret wilt wissen? Het systeem valt dan terug op de env-waarde (indien aanwezig).')) return;
    setMollieWebhookSaving(true);
    setMollieWebhookResult(null);
    try {
      const { data } = await adminApi.mollieWebhookConfigUpdate({ mollieWebhookSecret: '', clear: true });
      await reload();
      setMollieWebhookResult({ ok: true, message: `Secret gewist — actieve bron: ${data?.source || 'none'}` });
    } catch (e) {
      const status = e.response?.status;
      setMollieWebhookResult({ ok: false, message: e.response?.data?.detail || `Clear failed (HTTP ${status || 'unknown'})` });
    } finally {
      setMollieWebhookSaving(false);
    }
  };


  const saveBpost = async () => {
    setBpostSaving(true);
    setBpostResult(null);
    try {
      // Build patch: only send fields the admin actually typed.
      // Password-style header value: only send when the admin typed
      // a new value (blank preserves the existing one).
      const patch = {};
      const passthrough = [
        'bpostLocatorBaseUrl', 'bpostLocatorPath',
        'bpostSpPartnerId', 'bpostSpAppId',
        'bpostPlPartnerId', 'bpostPlAppId',
        'bpostLocatorSecurityHeaderName',
        'bpostSpTypeBitmask', 'bpostPlTypeBitmask',
        'bpostLocatorLanguage',
      ];
      passthrough.forEach(k => { patch[k] = bpostForm[k]; });
      if (bpostForm.bpostLocatorSecurityHeaderValue) {
        patch.bpostLocatorSecurityHeaderValue = bpostForm.bpostLocatorSecurityHeaderValue;
      }
      const { data } = await adminApi.bpostLocatorConfigUpdate(patch);
      await reload();
      const log = (data?.changeLog || []);
      const updated = log.filter(e => e.decision === 'updated').length;
      const blocked = log.filter(e => e.decision === 'blocked').length;
      const preserved = log.filter(e => e.decision === 'preserved').length;
      setBpostResult({
        ok: true,
        message: `Opgeslagen — bijgewerkt: ${updated}, behouden: ${preserved}, geblokkeerd: ${blocked}`,
      });
    } catch (e) {
      const status = e.response?.status;
      setBpostResult({ ok: false, message: e.response?.data?.detail || `Save failed (HTTP ${status || 'unknown'})` });
    } finally {
      setBpostSaving(false);
    }
  };

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-4 border-gray-200 border-t-gray-800 rounded-full animate-spin" /></div>;
  if (!data) return null;

  return (
    <div data-testid="ops-system" className="p-8">
      <div className="mb-6"><h1 className="text-2xl font-bold text-gray-900">System & Connectors</h1><p className="text-sm text-gray-500 mt-1">Integration configuration and status</p></div>

      {/* Yuki Config Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${yukiCfg?.yukiEnabled === 'true' && yukiCfg?.configComplete ? 'bg-green-500' : yukiCfg?.yukiApplicationKeyMasked ? 'bg-amber-500' : 'bg-gray-300'}`} />
            <h2 className="text-sm font-semibold text-gray-900">Yuki Accounting</h2>
            {yukiCfg?.yukiEnabled === 'true' && yukiCfg?.configComplete && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">Active</span>}
            {yukiCfg?.yukiEnabled !== 'true' && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-semibold">Disabled</span>}
          </div>
          <div className="flex gap-2">
            <button onClick={testYuki} disabled={testing} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${testing ? 'animate-spin' : ''}`} /> Test
            </button>
            <button onClick={saveYuki} disabled={saving} className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5">
              <Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Enabled</label>
            <select value={yukiForm.yukiEnabled} onChange={e => setYukiForm(f => ({...f, yukiEnabled: e.target.value}))} className={inputClass}>
              <option value="false">Disabled</option>
              <option value="true">Enabled</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Base URL</label>
            <input value={yukiForm.yukiBaseUrl} onChange={e => setYukiForm(f => ({...f, yukiBaseUrl: e.target.value}))} className={inputClass} placeholder="https://api.yukiworks.be" />
            <p className="text-xs text-gray-400 mt-1">Belgium: api.yukiworks.be — Netherlands: api.yukiworks.nl</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Application Key {yukiCfg?.yukiApplicationKeyMasked && <span className="text-gray-400 font-normal">(current: {yukiCfg.yukiApplicationKeyMasked})</span>}</label>
            <input type="password" value={yukiForm.yukiApplicationKey} onChange={e => setYukiForm(f => ({...f, yukiApplicationKey: e.target.value}))} className={inputClass} placeholder="Leave blank to keep current" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Company ID</label>
            <input value={yukiForm.yukiCompanyId} onChange={e => setYukiForm(f => ({...f, yukiCompanyId: e.target.value}))} className={inputClass} />
            <p className="text-xs text-gray-400 mt-1">Use the Yuki Company/Administration ID (GUID) from portal settings &gt; Web services. Not the domain slug.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Peppol Auto Send</label>
            <select data-testid="yuki-peppol-auto-send-select" value={yukiForm.yukiPeppolAutoSend} onChange={e => setYukiForm(f => ({...f, yukiPeppolAutoSend: e.target.value}))} className={inputClass}>
              <option value="false">Off (manual in Yuki)</option>
              <option value="true">Auto</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Auto = Commerce zet <code>&lt;SentToPeppol&gt;true&lt;/SentToPeppol&gt;</code> alléén voor B2B-facturen met geldig btw-nummer (BE/NL/FR/LU), alléén bij een expliciete Process Pending / Export Now van een nog nooit verzonden factuur. Geen historische verzending, geen scheduler. Yuki beslist of de ontvanger bereikbaar is; Commerce krijgt géén afleveringsbevestiging. Vereist Peppol-verzendrechten op het Yuki-domein.
            </p>
            {yukiForm.yukiPeppolAutoSend === 'true' && (
              <div className="mt-2" data-testid="yuki-peppol-confirm-wrap">
                <label className="block text-xs font-medium text-amber-700 mb-1">Bevestiging (typ exact: ENABLE PEPPOL AUTO SEND)</label>
                <input data-testid="yuki-peppol-confirm-input" value={peppolConfirmation} onChange={e => setPeppolConfirmation(e.target.value)} className={inputClass} placeholder="ENABLE PEPPOL AUTO SEND" />
              </div>
            )}
          </div>
        </div>
        {/* Connector health + export stats — separated */}
        {yukiCfg && (
          <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
            {/* Connector health */}
            <div className="flex items-center gap-2 text-xs">
              <div className={`w-2 h-2 rounded-full ${yukiCfg.connectorHealthy === true ? 'bg-green-500' : yukiCfg.connectorHealthy === false ? 'bg-red-500' : 'bg-gray-300'}`} />
              <span className="text-gray-600 font-medium">Connector:</span>
              <span className={yukiCfg.connectorHealthy ? 'text-green-700' : 'text-gray-500'}>
                {yukiCfg.connectorHealthy === true ? 'Authenticated' : yukiCfg.connectorHealthy === false ? 'Auth failed' : 'Not tested'}
              </span>
              {yukiCfg.lastTestAt && <span className="text-gray-400 ml-1">({new Date(yukiCfg.lastTestAt).toLocaleString('nl-BE')})</span>}
            </div>
            {/* Export job stats */}
            <div className="flex items-center gap-4 text-xs text-gray-400">
              {yukiCfg.lastSuccessfulExportAt && <span>Last export: {new Date(yukiCfg.lastSuccessfulExportAt).toLocaleString('nl-BE')}</span>}
              {yukiCfg.failedExportCount > 0 && <span data-testid="yuki-failed-export-count" className="text-amber-600">{yukiCfg.failedExportCount} failed export(s)</span>}
              {yukiCfg.lastExportErrorType && <span className="text-red-500">Last export error: {yukiCfg.lastExportErrorType}</span>}
              {/* Stale/orphan total — informational only. Renders only
                  when there's residue AND no actionable failures, so
                  the operator sees a calm gray hint instead of an
                  alarming amber number. */}
              {yukiCfg.failedExportStaleTotal > 0 && yukiCfg.failedExportCount === 0 && (
                <span data-testid="yuki-stale-export-total" className="text-gray-400" title="Orphan/archived export rows kept for audit. Not actionable.">
                  ({yukiCfg.failedExportStaleTotal} stale/orphan)
                </span>
              )}
            </div>
          </div>
        )}
        {testResult && (
          <div className={`mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {testResult.success ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {testResult.message}
          </div>
        )}
      </div>

      {/* Active Ants Config Card (v3 — canonical) */}
      <div data-testid="aa-connector-card" className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${aaCfg?.aaEnabled && aaCfg?.configComplete ? 'bg-green-500' : aaCfg?.aaBaseUrl ? 'bg-amber-500' : 'bg-gray-300'}`} />
            <h2 className="text-sm font-semibold text-gray-900">Active Ants Fulfilment (v3)</h2>
            {aaCfg?.aaEnabled && aaCfg?.configComplete && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">Active</span>}
            {!aaCfg?.aaEnabled && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-semibold">Disabled</span>}
            {aaCfg?.source && <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">{aaCfg.source}</span>}
            <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-semibold tracking-wider">READ-ONLY</span>
          </div>
          <div className="flex gap-2">
            <button data-testid="aa-test-btn" onClick={testAa} disabled={aaTesting} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${aaTesting ? 'animate-spin' : ''}`} /> Test
            </button>
            <button data-testid="aa-save-btn" onClick={saveAa} disabled={aaSaving} className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5">
              <Save className="w-3 h-3" /> {aaSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Enabled</label>
            <select data-testid="aa-enabled" value={aaForm.aaEnabled ? 'true' : 'false'} onChange={e => setAaForm(f => ({...f, aaEnabled: e.target.value === 'true'}))} className={inputClass}>
              <option value="false">Disabled</option>
              <option value="true">Enabled</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Base URL</label>
            <input data-testid="aa-base-url" value={aaForm.aaBaseUrl} onChange={e => setAaForm(f => ({...f, aaBaseUrl: e.target.value}))} className={inputClass} placeholder="https://shopapi.activeants.nl" />
            <p className="text-xs text-gray-400 mt-1">Server root only (no /v3 suffix). Resource calls add /v3/* automatically.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Username</label>
            <input data-testid="aa-username" value={aaForm.aaUsername} onChange={e => setAaForm(f => ({...f, aaUsername: e.target.value}))} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Password {aaCfg?.aaPasswordMasked && <span className="text-gray-400 font-normal">(current: {aaCfg.aaPasswordMasked})</span>}</label>
            <input data-testid="aa-password" type="password" value={aaForm.aaPassword} onChange={e => setAaForm(f => ({...f, aaPassword: e.target.value}))} className={inputClass} placeholder="Leave blank to keep current" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Mode</label>
            <select data-testid="aa-mode" value={aaForm.aaMode} disabled className={inputClass}>
              <option value="readonly">Read-only</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">Outbound write mode is intentionally disabled in this release.</p>
          </div>
        </div>
        {aaTestResult && (
          <div data-testid="aa-test-result" className={`mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${aaTestResult.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {aaTestResult.ok ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {aaTestResult.message}
          </div>
        )}
      </div>

      {/* Resend Email Config Card */}
      <div data-testid="email-connector-card" className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${emailCfg?.configComplete ? 'bg-green-500' : emailCfg?.resendApiKeyMasked ? 'bg-amber-500' : 'bg-gray-300'}`} />
            <h2 className="text-sm font-semibold text-gray-900">Resend Email</h2>
            {emailCfg?.configComplete && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">Active</span>}
            {!emailCfg?.configComplete && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-semibold">{emailCfg?.resendEnabled === 'true' ? 'Enabled' : 'Disabled'}</span>}
            {emailCfg?.source && <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">{emailCfg.source}</span>}
          </div>
          <div className="flex gap-2">
            <button data-testid="email-test-btn" onClick={testEmail} disabled={emailTesting} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${emailTesting ? 'animate-spin' : ''}`} /> Send test
            </button>
            <button data-testid="email-save-btn" onClick={saveEmail} disabled={emailSaving} className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5">
              <Save className="w-3 h-3" /> {emailSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Enabled</label>
            <select data-testid="email-enabled" value={emailForm.resendEnabled} onChange={e => setEmailForm(f => ({...f, resendEnabled: e.target.value}))} className={inputClass}>
              <option value="false">Disabled (console-only)</option>
              <option value="true">Enabled (send via Resend)</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">When disabled, transactional emails are logged but not delivered.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">API Key {emailCfg?.resendApiKeyMasked && <span className="text-gray-400 font-normal">(current: {emailCfg.resendApiKeyMasked})</span>}</label>
            <input data-testid="email-api-key" type="password" value={emailForm.resendApiKey} onChange={e => setEmailForm(f => ({...f, resendApiKey: e.target.value}))} className={inputClass} placeholder="Leave blank to keep current — starts with re_" />
            <p className="text-xs text-gray-400 mt-1">Get one at resend.com/api-keys. Stored masked; never returned raw.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Sender email</label>
            <input data-testid="email-sender-email" value={emailForm.senderEmail} onChange={e => setEmailForm(f => ({...f, senderEmail: e.target.value}))} className={inputClass} placeholder="orders@herborist.be" />
            <p className="text-xs text-gray-400 mt-1">Must be a domain verified in your Resend account.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Sender name</label>
            <input data-testid="email-sender-name" value={emailForm.senderName} onChange={e => setEmailForm(f => ({...f, senderName: e.target.value}))} className={inputClass} placeholder="The Herborist" />
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">Reply-to (optional)</label>
            <input data-testid="email-reply-to" value={emailForm.replyToEmail} onChange={e => setEmailForm(f => ({...f, replyToEmail: e.target.value}))} className={inputClass} placeholder="hello@herborist.be" />
          </div>
          <div className="lg:col-span-2 pt-2 border-t border-gray-100">
            <label className="block text-xs font-medium text-gray-500 mb-1">Test recipient</label>
            <input data-testid="email-test-recipient" value={emailRecipient} onChange={e => setEmailRecipient(e.target.value)} className={inputClass} placeholder="Defaults to your admin email — override to send elsewhere" />
            <p className="text-xs text-gray-400 mt-1">Click "Send test" above to dispatch a one-off test message.</p>
          </div>
        </div>
        {emailTestResult && (
          <div data-testid="email-test-result" className={`mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${emailTestResult.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {emailTestResult.ok ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {emailTestResult.message}
          </div>
        )}
      </div>

      {/* Mollie Payments Config Card */}
      <div data-testid="mollie-connector-card" className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${mollieCfg?.configured ? (mollieCfg?.mode === 'live' ? 'bg-red-500' : 'bg-green-500') : 'bg-gray-300'}`} />
            <h2 className="text-sm font-semibold text-gray-900">Mollie Payments</h2>
            {mollieCfg?.configured && mollieCfg?.mode === 'test' && (
              <span data-testid="mollie-mode-badge" className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">TEST mode</span>
            )}
            {mollieCfg?.configured && mollieCfg?.mode === 'live' && (
              <span data-testid="mollie-mode-badge" className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-semibold">LIVE mode</span>
            )}
            {!mollieCfg?.configured && (
              <span data-testid="mollie-mode-badge" className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-semibold">Niet geconfigureerd</span>
            )}
            {mollieCfg?.source && <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">{mollieCfg.source}</span>}
          </div>
          <div className="flex gap-2">
            {mollieCfg?.source === 'system_config' && (
              <button data-testid="mollie-clear-btn" onClick={clearMollie} disabled={mollieSaving} className="px-3 py-1.5 border border-red-200 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5">
                <Trash2 className="w-3 h-3" /> Wissen
              </button>
            )}
            <button data-testid="mollie-save-btn" onClick={saveMollie} disabled={mollieSaving} className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5">
              <Save className="w-3 h-3" /> {mollieSaving ? 'Bezig...' : 'Opslaan'}
            </button>
          </div>
        </div>
        {mollieCfg?.mode === 'live' && (
          <div className="mb-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-800">
            <AlertTriangle className="w-3 h-3" />
            LIVE mode actief — echte betalingen worden verwerkt. Voor preview/testing gebruik je een <code>test_…</code>-sleutel.
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Mollie API Key {mollieCfg?.mollieApiKeyMasked && <span className="text-gray-400 font-normal">(huidige: {mollieCfg.mollieApiKeyMasked})</span>}</label>
            <input
              data-testid="mollie-api-key"
              type="password"
              autoComplete="off"
              value={mollieForm.mollieApiKey}
              onChange={e => setMollieForm({ mollieApiKey: e.target.value })}
              className={inputClass}
              placeholder="test_… of live_… — laat leeg om huidige te behouden"
            />
            <p className="text-xs text-gray-400 mt-1">
              Te vinden in <a href="https://my.mollie.com/dashboard/developers/api-keys" target="_blank" rel="noopener noreferrer" className="underline">my.mollie.com → Developers → API keys</a>. Wordt versleuteld in <code>system_config</code> opgeslagen; raw key wordt nooit teruggegeven door de API.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <div className="text-sm">
              {mollieCfg?.configured ? (
                <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-600" /> Geconfigureerd, modus <strong>{mollieCfg.mode?.toUpperCase()}</strong></span>
              ) : (
                <span className="inline-flex items-center gap-1.5"><XCircle className="w-4 h-4 text-gray-400" /> Geen geldige sleutel — checkout zal in test-mode bevestigen (alleen development).</span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Resolutievolgorde: <code>system_config.mollieApiKey</code> → env <code>MOLLIE_API_KEY</code> → niet geconfigureerd.
            </p>
          </div>
        </div>
        {mollieResult && (
          <div data-testid="mollie-result" className={`mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${mollieResult.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {mollieResult.ok ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {mollieResult.message}
          </div>
        )}
      </div>

      {/* Mollie Webhook Secret Card (P0-2b) */}
      <div data-testid="mollie-webhook-secret-card" className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${mollieWebhookCfg?.configured ? 'bg-green-500' : 'bg-gray-300'}`} />
            <h2 className="text-sm font-semibold text-gray-900">Mollie webhook secret</h2>
            {mollieWebhookCfg?.configured ? (
              <span data-testid="mollie-webhook-status-badge" className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">Configured</span>
            ) : (
              <span data-testid="mollie-webhook-status-badge" className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-semibold">Niet geconfigureerd</span>
            )}
            {mollieWebhookCfg?.source && (
              <span data-testid="mollie-webhook-source-badge" className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">{mollieWebhookCfg.source}</span>
            )}
          </div>
          <div className="flex gap-2">
            {mollieWebhookCfg?.hasValue && (
              <button data-testid="mollie-webhook-clear-btn" onClick={clearMollieWebhook} disabled={mollieWebhookSaving} className="px-3 py-1.5 border border-red-200 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5">
                <Trash2 className="w-3 h-3" /> Wissen
              </button>
            )}
            <button data-testid="mollie-webhook-save-btn" onClick={saveMollieWebhook} disabled={mollieWebhookSaving} className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5">
              <Save className="w-3 h-3" /> {mollieWebhookSaving ? 'Bezig...' : 'Opslaan'}
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Beschermt de Mollie webhook-URL tegen forged callbacks. <strong>Env <code>MOLLIE_WEBHOOK_SECRET</code> heeft prioriteit</strong>; deze Ops-waarde is een fallback wanneer de env-key niet via Deployments → Secrets gezet kan worden.
          {mollieWebhookCfg?.source === 'env' && (
            <> De env-key is momenteel actief — een waarde hier wordt opgeslagen maar <strong>niet gebruikt</strong> tot de env-key wordt verwijderd.</>
          )}
          {mollieWebhookCfg?.source === 'system_config_invalid' && (
            <> ⚠ De opgeslagen fallback is ongeldig (te kort of placeholder). Sla een nieuwe waarde op.</>
          )}
          {mollieWebhookCfg?.source === 'env_invalid' && (
            <> ⚠ De env-waarde is gezet maar ongeldig (te kort). Corrigeer de env-key in Deployments → Secrets.</>
          )}
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Nieuwe webhook secret
              {mollieWebhookCfg?.mollieWebhookSecretMasked && (
                <span className="text-gray-400 font-normal"> (huidige fallback: {mollieWebhookCfg.mollieWebhookSecretMasked})</span>
              )}
            </label>
            <input
              data-testid="mollie-webhook-secret-input"
              type="password"
              autoComplete="off"
              value={mollieWebhookForm.mollieWebhookSecret}
              onChange={e => setMollieWebhookForm({ mollieWebhookSecret: e.target.value })}
              className={inputClass}
              placeholder="Plak nieuwe webhook secret (≥ 24 tekens)"
              minLength={24}
            />
            <p className="text-xs text-gray-400 mt-1">
              Minimaal 24 tekens. Genereer lokaal met <code>python -c &quot;import secrets; print(secrets.token_urlsafe(48))&quot;</code>. Leeg laten = bestaande waarde behouden. Productie weigert placeholder-/test-achtige waarden.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <div className="text-sm">
              {mollieWebhookCfg?.configured ? (
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Actieve bron: <strong>{mollieWebhookCfg.source}</strong>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <XCircle className="w-4 h-4 text-gray-400" />
                  Geen geldige secret — Mollie webhook geeft 401 tot er een waarde gezet wordt.
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Resolutievolgorde: env <code>MOLLIE_WEBHOOK_SECRET</code> → <code>system_config.mollieWebhookSecret</code> → niet geconfigureerd.
            </p>
          </div>
        </div>
        {mollieWebhookResult && (
          <div data-testid="mollie-webhook-result" className={`mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${mollieWebhookResult.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {mollieWebhookResult.ok ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {mollieWebhookResult.message}
          </div>
        )}
      </div>


      {/* bpost GeoLocator Config Card */}
      <div data-testid="bpost-connector-card" className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${bpostCfg?.configured ? 'bg-green-500' : (bpostCfg?.spConfigured || bpostCfg?.plConfigured || bpostCfg?.securityHeaderPresent) ? 'bg-amber-500' : 'bg-gray-300'}`} />
            <h2 className="text-sm font-semibold text-gray-900">bpost GeoLocator</h2>
            {bpostCfg?.configured && <span data-testid="bpost-status-badge" className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">Configured</span>}
            {!bpostCfg?.configured && <span data-testid="bpost-status-badge" className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-semibold">Niet volledig geconfigureerd</span>}
            <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-semibold tracking-wider">PREVIEW</span>
            {bpostCfg?.source && <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">{bpostCfg.source}</span>}
          </div>
          <div className="flex gap-2">
            <button data-testid="bpost-save-btn" onClick={saveBpost} disabled={bpostSaving} className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5">
              <Save className="w-3 h-3" /> {bpostSaving ? 'Bezig...' : 'Opslaan'}
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Per bpack-handleiding v3.3.34 §B.4.1. Geen smoke-test of uitgaande call vanuit deze pagina — alleen configuratie.
          Lege/maskeerde velden behouden de huidige waarde. Test-achtige waarden over een echte bestaande waarde worden geblokkeerd door de credential guard.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Base URL</label>
            <input data-testid="bpost-base-url" value={bpostForm.bpostLocatorBaseUrl} onChange={e => setBpostForm(f => ({...f, bpostLocatorBaseUrl: e.target.value}))} className={inputClass} placeholder="https://pudo.bpost.cloud" />
            <p className="text-xs text-gray-400 mt-1">Standaard: <code>https://pudo.bpost.cloud</code></p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Path</label>
            <input data-testid="bpost-path" value={bpostForm.bpostLocatorPath} onChange={e => setBpostForm(f => ({...f, bpostLocatorPath: e.target.value}))} className={inputClass} placeholder="/Locator/" />
            <p className="text-xs text-gray-400 mt-1">Per bpack-handleiding §B.4.1.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Service-Point PartnerID</label>
            <input data-testid="bpost-sp-partner" value={bpostForm.bpostSpPartnerId} onChange={e => setBpostForm(f => ({...f, bpostSpPartnerId: e.target.value}))} className={inputClass} placeholder="Regi-verstrekt" autoComplete="off" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Service-Point AppID</label>
            <input data-testid="bpost-sp-app" value={bpostForm.bpostSpAppId} onChange={e => setBpostForm(f => ({...f, bpostSpAppId: e.target.value}))} className={inputClass} placeholder="Regi-verstrekt" autoComplete="off" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Parcel-Locker PartnerID</label>
            <input data-testid="bpost-pl-partner" value={bpostForm.bpostPlPartnerId} onChange={e => setBpostForm(f => ({...f, bpostPlPartnerId: e.target.value}))} className={inputClass} placeholder="Regi-verstrekt" autoComplete="off" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Parcel-Locker AppID</label>
            <input data-testid="bpost-pl-app" value={bpostForm.bpostPlAppId} onChange={e => setBpostForm(f => ({...f, bpostPlAppId: e.target.value}))} className={inputClass} placeholder="Regi-verstrekt" autoComplete="off" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Security header name</label>
            <input data-testid="bpost-sec-name" value={bpostForm.bpostLocatorSecurityHeaderName} onChange={e => setBpostForm(f => ({...f, bpostLocatorSecurityHeaderName: e.target.value}))} className={inputClass} placeholder="x-api-key" />
            <p className="text-xs text-gray-400 mt-1">Standaard: <code>x-api-key</code> (§B.4.1.0, v3.3.29+).</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Security header value
              {bpostCfg?.bpostLocatorSecurityHeaderValueMasked && <span className="text-gray-400 font-normal"> (huidige: {bpostCfg.bpostLocatorSecurityHeaderValueMasked})</span>}
            </label>
            <input data-testid="bpost-sec-value" type="password" autoComplete="off" value={bpostForm.bpostLocatorSecurityHeaderValue} onChange={e => setBpostForm(f => ({...f, bpostLocatorSecurityHeaderValue: e.target.value}))} className={inputClass} placeholder="Laat leeg om huidige waarde te behouden" />
            <p className="text-xs text-gray-400 mt-1">bpost/eSolutions-verstrekte API-sleutel. Wordt versleuteld opgeslagen; raw value wordt nooit teruggegeven door de API.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">SP type-bitmask</label>
            <input data-testid="bpost-sp-bitmask" value={bpostForm.bpostSpTypeBitmask} onChange={e => setBpostForm(f => ({...f, bpostSpTypeBitmask: e.target.value}))} className={inputClass} placeholder="19" />
            <p className="text-xs text-gray-400 mt-1">19 = postkantoor (1) + postpunt (2) + Parcel Point (16).</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">PL type-bitmask</label>
            <input data-testid="bpost-pl-bitmask" value={bpostForm.bpostPlTypeBitmask} onChange={e => setBpostForm(f => ({...f, bpostPlTypeBitmask: e.target.value}))} className={inputClass} placeholder="4" />
            <p className="text-xs text-gray-400 mt-1">4 = bpack 24/7 / parcel locker.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Taal</label>
            <select data-testid="bpost-language" value={bpostForm.bpostLocatorLanguage || 'NL'} onChange={e => setBpostForm(f => ({...f, bpostLocatorLanguage: e.target.value}))} className={inputClass}>
              <option value="NL">NL</option>
              <option value="FR">FR</option>
              <option value="DE">DE</option>
              <option value="EN">EN</option>
            </select>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-1 lg:grid-cols-3 gap-2 text-xs">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${bpostCfg?.spConfigured ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-gray-600">Service-point credentials: <strong>{bpostCfg?.spConfigured ? 'compleet' : 'niet ingesteld'}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${bpostCfg?.plConfigured ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-gray-600">Parcel-locker credentials: <strong>{bpostCfg?.plConfigured ? 'compleet' : 'niet ingesteld'}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${bpostCfg?.securityHeaderPresent ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-gray-600">Security header: <strong>{bpostCfg?.securityHeaderPresent ? 'aanwezig' : 'leeg'}</strong></span>
          </div>
        </div>
        {bpostResult && (
          <div data-testid="bpost-result" className={`mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${bpostResult.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {bpostResult.ok ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {bpostResult.message}
          </div>
        )}
      </div>

      {/* Connector Audit Timeline */}
      <ConnectorAuditCard events={auditEvents} />

      {/* Pre-live Cleanup — Mollie TEST Order 2600001654 (Option A: keep & archive) */}
      <PrelaunchCleanupCard />

      {/* Other Connectors */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Connectors</h2>
        <div className="space-y-3">
          {data.connectors.filter(c => c.name !== 'Yuki Accounting').map(c => {
            const Icon = CONNECTOR_ICONS[c.status] || AlertTriangle;
            const color = CONNECTOR_COLORS[c.status] || 'text-gray-400 bg-gray-50';
            return (
              <div key={c.name} className="flex items-center gap-4 p-3 rounded-lg bg-gray-50/50">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-4 h-4" /></div>
                <div className="flex-1"><p className="text-sm font-semibold text-gray-900">{c.name}</p><p className="text-xs text-gray-500">{c.detail}</p></div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${color}`}>{c.status.replace('_', ' ')}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Imports */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Import Channels</h2>
        <div className="space-y-3">
          {data.imports.map(imp => (
            <div key={imp.name} className="flex items-center justify-between p-3 rounded-lg bg-gray-50/50">
              <div><p className="text-sm font-semibold text-gray-900">{imp.name}</p><p className="text-xs text-gray-500">{imp.detail}</p></div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${imp.status === 'available' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{imp.status.replace('_', ' ')}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Test Data Cleanup */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Test Data Cleanup</h2>
        <p className="text-xs text-gray-500 mb-3">Remove test/dev invoices and exports before Yuki activation. Matches: test emails, "Test" names, admin/dev native orders, tr_test_ payments.</p>
        <div className="flex gap-2">
          <button onClick={previewCleanup} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" /> Preview Cleanup
          </button>
        </div>
        {cleanupPreview && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800 font-semibold mb-2">Found {cleanupPreview.invoiceCount} test/dev invoices + {cleanupPreview.exportCount} linked exports</p>
            {cleanupPreview.categories?.map(cat => cat.count > 0 && (
              <div key={cat.label} className="mb-2">
                <p className="text-xs text-amber-700 font-semibold">{cat.label}: {cat.count}</p>
                <div className="ml-3 text-xs text-amber-600 space-y-0.5">
                  {cat.records?.map(r => (
                    <p key={r.id}>{r.invoiceNumber} — {r.customerName}</p>
                  ))}
                </div>
              </div>
            ))}
            {cleanupPreview.invoiceCount > 0 && (
              <button onClick={executeCleanup} disabled={cleaning}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5">
                <Trash2 className="w-3 h-3" /> {cleaning ? 'Deleting...' : `Delete ${cleanupPreview.invoiceCount} records`}
              </button>
            )}
            {cleanupPreview.invoiceCount === 0 && <p className="text-xs text-green-700 mt-1">No test/dev records found — clean!</p>}
          </div>
        )}
        {cleanupResult && (
          <div className="mt-3 text-xs bg-green-50 border border-green-200 text-green-800 rounded-lg px-3 py-2">
            Cleaned: {cleanupResult.deletedInvoices} invoices, {cleanupResult.deletedExports} exports
          </div>
        )}
      </div>

      {/* Recent webhooks */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Recent Webhook Events</h2>
        {data.recentWebhooks.length === 0 ? (
          <p className="text-sm text-gray-400">No webhook events recorded</p>
        ) : (
          <div className="space-y-2">
            {data.recentWebhooks.map(w => (
              <div key={w._id} className="flex items-center gap-3 text-sm p-2 rounded-lg hover:bg-gray-50">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${WEBHOOK_COLORS[w.status] || 'bg-gray-100 text-gray-500'}`}>{w.status}</span>
                <span className="font-mono text-xs text-gray-600">{w.externalId}</span>
                <span className="text-xs text-gray-400 ml-auto">{w.created_at ? new Date(w.created_at).toLocaleString('nl-BE') : ''}</span>
                {w.errorMessage && <span className="text-xs text-red-500 truncate max-w-[200px]">{w.errorMessage}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Connector Audit Timeline ─────────────────────────────────────────
// Compact, read-only feed of recent credential change events. The
// backend strips raw secret values; this card only renders metadata.
function ConnectorAuditCard({ events }) {
  const decisionStyle = {
    updated: 'bg-emerald-100 text-emerald-700',
    preserved: 'bg-slate-100 text-slate-600',
    blocked: 'bg-amber-100 text-amber-800',
  };
  return (
    <div data-testid="connector-audit-card" className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Connector Audit Timeline</h2>
        </div>
        <span className="text-[10px] text-gray-400">No secret values shown</span>
      </div>
      {(!events || events.length === 0) ? (
        <p data-testid="connector-audit-empty" className="text-sm text-gray-400">No connector credential changes recorded yet.</p>
      ) : (
        <div className="space-y-2">
          {events.map(ev => {
            const styleCls = decisionStyle[ev.decision] || 'bg-gray-100 text-gray-500';
            const fields = (ev.changedFields || []).concat(ev.preservedFields || []).concat(ev.blockedFields || []);
            const at = ev.at ? new Date(ev.at).toLocaleString('nl-BE') : '';
            return (
              <div key={ev.id} data-testid={`audit-event-${ev.id}`}
                className="flex items-center gap-3 text-sm p-2 rounded-lg hover:bg-gray-50">
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${styleCls}`}>
                  {ev.decision}
                </span>
                <span className="text-xs font-semibold text-gray-800">{ev.connector || 'unknown'}</span>
                <span className="text-[11px] text-gray-500 font-mono truncate max-w-[280px]" title={fields.join(', ')}>
                  {fields.slice(0, 4).join(', ')}{fields.length > 4 ? '…' : ''}
                </span>
                <span className="text-[10px] text-gray-400 ml-auto">{ev.source || '—'}</span>
                <span className="text-[10px] text-gray-400">{at}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ── Pre-live Cleanup Card ────────────────────────────────────────────
// Strictly scoped to Mollie TEST order 2600001654 (Option A —
// keep & archive). Three actions: Diagnose (read-only), Dry-run
// (validates guards, no writes), Apply (commits the soft-archive
// after explicit confirmation). Apply is disabled until a green
// dry-run has been observed. The component renders raw blockers,
// warnings, planned changes and the untouched-collections list so
// the operator has a full audit surface before committing.
const PRELAUNCH_CLEANUP_TARGET = '2600001654';
const PRELAUNCH_CLEANUP_REASON =
  'controlled production Mollie TEST smoke before live cutover';
// Operator-confirmed Mollie TEST override constants. These mirror
// the canonical values enforced by the backend service. The
// confirmation is specific to this exact payment / order / amount
// — any drift here causes the backend to refuse the override.
const PRELAUNCH_OVERRIDE_PAYMENT_ID = 'tr_8yyK6RxoCDDVwsLAk7RRJ';
const PRELAUNCH_OVERRIDE_AMOUNT_EUR = 40.0;
const PRELAUNCH_OVERRIDE_CONFIRMATION_TEXT =
  'I confirm this payment was created during the controlled Mollie ' +
  'TEST smoke and must be archived without stock reversal or counter reset.';

function PrelaunchCleanupCard() {
  const [diagnosis, setDiagnosis] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [busy, setBusy] = useState(null); // 'diagnose' | 'dryrun' | 'apply' | null
  const [error, setError] = useState(null);
  const [overrideEnabled, setOverrideEnabled] = useState(false);

  const target = PRELAUNCH_CLEANUP_TARGET;

  // Build the override payload only when the operator has explicitly
  // ticked the checkbox. The fields are canonical and not editable
  // from the UI — the backend re-validates every one.
  const buildOverride = () => (
    overrideEnabled
      ? {
        operatorConfirmedMollieTest: true,
        confirmedOrderNumber: target,
        confirmedPaymentId: PRELAUNCH_OVERRIDE_PAYMENT_ID,
        confirmedAmount: PRELAUNCH_OVERRIDE_AMOUNT_EUR,
        reason: PRELAUNCH_CLEANUP_REASON,
      }
      : null
  );

  const runDiagnose = async () => {
    setBusy('diagnose'); setError(null);
    try {
      const r = await adminApi.prelaunchCleanupDiagnose(target);
      setDiagnosis(r.data);
      // Invalidate stale dry-run so apply re-requires a fresh dry-run.
      setDryRun(null);
      setApplyResult(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'diagnose failed');
    } finally {
      setBusy(null);
    }
  };

  const runDryRun = async () => {
    setBusy('dryrun'); setError(null);
    try {
      const override = buildOverride();
      const payload = override ? { operatorConfirmation: override } : {};
      const r = await adminApi.prelaunchCleanupDryRun(target, payload);
      setDryRun(r.data);
      setApplyResult(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'dry-run failed');
    } finally {
      setBusy(null);
    }
  };

  const runApply = async () => {
    if (!dryRun || dryRun.canApply !== true) {
      setError('Apply requires a successful dry-run first.');
      return;
    }
    const confirmed = window.confirm(
      `Archive Mollie TEST order ${target} and its sales invoice?\n\n` +
      'This sets archived=true on orders + sales_invoices and writes ' +
      '2 audit rows. It does NOT touch payments, stock, order_lines, ' +
      'webhook_events, accounting_exports, AA rows or the canonical ' +
      'counter, and makes NO external calls (Mollie/Yuki/AA/Resend/bpost).'
    );
    if (!confirmed) return;
    setBusy('apply'); setError(null);
    try {
      const override = buildOverride();
      const payload = {
        apply: true,
        orderNumber: target,
        reason: PRELAUNCH_CLEANUP_REASON,
      };
      if (override) payload.operatorConfirmation = override;
      const r = await adminApi.prelaunchCleanupApply(target, payload);
      setApplyResult(r.data);
      // Force a fresh dry-run before any subsequent apply.
      setDryRun(null);
    } catch (e) {
      // 409 on apply-time guard rerun returns the structured result
      // as the `detail` payload.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.blockers)) {
        setApplyResult(detail);
      } else {
        setError(typeof detail === 'string' ? detail : (e.message || 'apply failed'));
      }
    } finally {
      setBusy(null);
    }
  };

  const canApply = dryRun && dryRun.canApply === true && dryRun.alreadyArchived !== true;
  const archived =
    (diagnosis && diagnosis.alreadyArchived === true) ||
    (dryRun && dryRun.alreadyArchived === true) ||
    (applyResult && (applyResult.alreadyArchived === true ||
                     applyResult.mutationPerformed === true));

  return (
    <div
      data-testid="prelaunch-cleanup-card"
      className="bg-white rounded-xl border border-gray-200 p-6 mb-6"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-amber-600" />
          <h2 className="text-sm font-semibold text-gray-900">
            Pre-live Cleanup — Mollie TEST Order {target}
          </h2>
          {archived && (
            <span
              data-testid="prelaunch-cleanup-archived-badge"
              className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-semibold"
            >
              Archived
            </span>
          )}
        </div>
        <span className="text-[10px] text-gray-400 font-mono">
          Option A · keep &amp; archive
        </span>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Soft-archives order <span className="font-mono">{target}</span> and
        its sales invoice. Refuses if a Yuki export, an Active Ants sync
        or shipment, or a LIVE-shaped Mollie payment is present.
        Counter, stock, payments, order lines, webhook events and AA rows
        are <span className="font-semibold">never</span> mutated. No
        external calls are made.
      </p>

      {/* Operator-confirmed Mollie TEST override. Only needed when the
          payment row lacks `mode`/`test` markers (production case). The
          backend re-validates every field — checking the box is not
          enough; the diagnosed payment id, order number and amount
          must all match the canonical values shown below. */}
      <div
        data-testid="prelaunch-cleanup-override-block"
        className={`text-xs border rounded-lg p-3 mb-4 ${
          overrideEnabled
            ? 'bg-amber-50 border-amber-200'
            : 'bg-gray-50 border-gray-100'
        }`}
      >
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            data-testid="prelaunch-cleanup-override-checkbox"
            type="checkbox"
            className="mt-0.5"
            checked={overrideEnabled}
            onChange={(e) => {
              setOverrideEnabled(e.target.checked);
              // Invalidate any prior dry-run / apply so the operator
              // explicitly re-runs after toggling.
              setDryRun(null);
              setApplyResult(null);
            }}
          />
          <div className="flex-1">
            <div className="font-semibold text-gray-800 mb-1">
              Operator-confirmed Mollie TEST mode
            </div>
            <div className="text-gray-600 mb-2">
              {PRELAUNCH_OVERRIDE_CONFIRMATION_TEXT}
            </div>
            <div
              data-testid="prelaunch-cleanup-override-constants"
              className="font-mono text-[10px] text-gray-500 space-y-0.5"
            >
              <div>orderNumber: {target}</div>
              <div>molliePaymentId: {PRELAUNCH_OVERRIDE_PAYMENT_ID}</div>
              <div>
                amount: € {PRELAUNCH_OVERRIDE_AMOUNT_EUR.toFixed(2)}
              </div>
              <div>reason: {PRELAUNCH_CLEANUP_REASON}</div>
            </div>
            <div className="text-[10px] text-amber-700 mt-2">
              Override only suppresses the “no positive TEST markers”
              blocker. It NEVER bypasses LIVE markers, Yuki exports,
              Active Ants sync/shipment rows, or any other guard.
              The backend re-validates every field and refuses if
              anything drifts.
            </div>
          </div>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          data-testid="prelaunch-cleanup-diagnose-btn"
          onClick={runDiagnose}
          disabled={busy !== null}
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
        >
          <RefreshCw className={`w-3 h-3 ${busy === 'diagnose' ? 'animate-spin' : ''}`} />
          {busy === 'diagnose' ? 'Diagnosing…' : 'Diagnose'}
        </button>
        <button
          data-testid="prelaunch-cleanup-dryrun-btn"
          onClick={runDryRun}
          disabled={busy !== null}
          className="text-xs px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 flex items-center gap-1"
        >
          <RefreshCw className={`w-3 h-3 ${busy === 'dryrun' ? 'animate-spin' : ''}`} />
          {busy === 'dryrun' ? 'Validating…' : `Dry-run${overrideEnabled ? ' (with confirm)' : ''}`}
        </button>
        <button
          data-testid="prelaunch-cleanup-apply-btn"
          onClick={runApply}
          disabled={busy !== null || !canApply}
          title={!canApply ? 'Run a successful dry-run first' : 'Commit archive'}
          className="text-xs px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 font-semibold"
        >
          <Trash2 className="w-3 h-3" />
          {busy === 'apply' ? 'Applying…' : 'Apply (archive)'}
        </button>
      </div>

      {error && (
        <div
          data-testid="prelaunch-cleanup-error"
          className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2 mb-3 flex items-center gap-1"
        >
          <XCircle className="w-3 h-3" /> {String(error)}
        </div>
      )}

      {diagnosis && (
        <div
          data-testid="prelaunch-cleanup-diagnose-result"
          className="text-xs bg-gray-50 border border-gray-100 rounded-lg p-3 mb-3 space-y-1"
        >
          <div className="font-semibold text-gray-700 mb-1">
            Diagnose · target {diagnosis.target}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono">
            <div>order: {String(diagnosis.found?.order)}</div>
            <div>salesInvoice: {String(diagnosis.found?.salesInvoice)}</div>
            <div>payments: {diagnosis.found?.payments}</div>
            <div>accountingExports: {diagnosis.found?.accountingExports}</div>
            <div>aaOrderSyncs: {diagnosis.found?.aaOrderSyncs}</div>
            <div>aaShipments: {diagnosis.found?.aaShipments}</div>
            <div>orderLines: {diagnosis.found?.orderLines}</div>
            <div>webhookEvents: {diagnosis.found?.webhookEvents}</div>
            <div>archived: {String(diagnosis.alreadyArchived)}</div>
            <div>mutation: {String(diagnosis.mutationPerformed)}</div>
          </div>
          {(diagnosis.blockers || []).length > 0 && (
            <BlockerList
              testid="prelaunch-cleanup-diagnose-blockers"
              items={diagnosis.blockers}
              label="Blockers"
            />
          )}
          {(diagnosis.warnings || []).length > 0 && (
            <BlockerList
              testid="prelaunch-cleanup-diagnose-warnings"
              items={diagnosis.warnings}
              label="Warnings"
              tone="amber"
            />
          )}
        </div>
      )}

      {dryRun && (
        <div
          data-testid="prelaunch-cleanup-dryrun-result"
          className={`text-xs border rounded-lg p-3 mb-3 space-y-1 ${
            dryRun.canApply ? 'bg-blue-50 border-blue-100' : 'bg-red-50 border-red-100'
          }`}
        >
          <div className="font-semibold text-gray-700 mb-1 flex items-center gap-1">
            {dryRun.canApply ? (
              <CheckCircle className="w-3 h-3 text-blue-600" />
            ) : (
              <XCircle className="w-3 h-3 text-red-600" />
            )}
            Dry-run · canApply={String(dryRun.canApply)}
            {dryRun.alreadyArchived && (
              <span data-testid="prelaunch-cleanup-already-archived" className="ml-1 text-emerald-700">
                · alreadyArchived
              </span>
            )}
            <span className="ml-2 text-gray-500">
              mutationPerformed={String(dryRun.mutationPerformed)}
            </span>
          </div>
          {(dryRun.blockers || []).length > 0 && (
            <BlockerList
              testid="prelaunch-cleanup-dryrun-blockers"
              items={dryRun.blockers}
              label="Blockers"
            />
          )}
          {(dryRun.warnings || []).length > 0 && (
            <BlockerList
              testid="prelaunch-cleanup-dryrun-warnings"
              items={dryRun.warnings}
              label="Warnings"
              tone="amber"
            />
          )}
          {dryRun.plannedChanges && (
            <details className="mt-1">
              <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
                Planned changes ({dryRun.plannedChanges.auditRows?.length || 0} audit rows)
              </summary>
              <pre data-testid="prelaunch-cleanup-planned-changes"
                className="mt-1 p-2 bg-white border border-gray-100 rounded font-mono text-[10px] overflow-x-auto">
                {JSON.stringify(dryRun.plannedChanges, null, 2)}
              </pre>
            </details>
          )}
          {(dryRun.untouchedCollections || []).length > 0 && (
            <div
              data-testid="prelaunch-cleanup-untouched-collections"
              className="mt-1 text-[10px] text-gray-500"
            >
              Untouched collections:{' '}
              <span className="font-mono">
                {(dryRun.untouchedCollections || []).join(', ')}
              </span>
            </div>
          )}
        </div>
      )}

      {applyResult && (
        <div
          data-testid="prelaunch-cleanup-apply-result"
          className={`text-xs border rounded-lg p-3 mb-1 space-y-1 ${
            applyResult.ok === false
              ? 'bg-red-50 border-red-100'
              : 'bg-emerald-50 border-emerald-100'
          }`}
        >
          <div className="font-semibold text-gray-700 mb-1 flex items-center gap-1">
            {applyResult.ok === false ? (
              <XCircle className="w-3 h-3 text-red-600" />
            ) : (
              <CheckCircle className="w-3 h-3 text-emerald-600" />
            )}
            Apply · ok={String(applyResult.ok ?? true)} · mutationPerformed={String(applyResult.mutationPerformed)}
            {applyResult.alreadyArchived && (
              <span className="ml-1 text-emerald-700">· alreadyArchived (no-op)</span>
            )}
          </div>
          {applyResult.mutatedCollections && (
            <div className="font-mono text-[11px]">
              mutated: {(applyResult.mutatedCollections || []).join(', ') || '—'}
            </div>
          )}
          {applyResult.modifiedCounts && (
            <div className="font-mono text-[11px]">
              orders={applyResult.modifiedCounts.orders ?? 0} ·{' '}
              sales_invoices={applyResult.modifiedCounts.sales_invoices ?? 0} ·{' '}
              system_audit_log={applyResult.modifiedCounts.system_audit_log ?? 0}
            </div>
          )}
          {(applyResult.blockers || []).length > 0 && (
            <BlockerList
              testid="prelaunch-cleanup-apply-blockers"
              items={applyResult.blockers}
              label="Apply-time blockers"
            />
          )}
          {applyResult.reminder && (
            <div className="text-[10px] text-gray-500 mt-1">
              {applyResult.reminder}
            </div>
          )}
          {applyResult.untouchedCollections && (
            <div className="text-[10px] text-gray-500">
              Untouched:{' '}
              <span className="font-mono">
                {(applyResult.untouchedCollections || []).join(', ')}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BlockerList({ items, label, testid, tone = 'red' }) {
  const palette = tone === 'amber'
    ? 'bg-amber-50 border-amber-100 text-amber-800'
    : 'bg-red-50 border-red-100 text-red-700';
  return (
    <div data-testid={testid} className={`text-[11px] border rounded p-2 mt-1 ${palette}`}>
      <div className="font-semibold uppercase tracking-wider text-[10px] mb-0.5">
        {label} ({items.length})
      </div>
      <ul className="list-disc ml-4 space-y-0.5 font-mono">
        {items.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
    </div>
  );
}
